import { Css, useComputed, useTestIds } from "@homebound/beam";
import { Fragment, type ReactNode } from "react";
import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import { planPackageAiSourcesRoute } from "src/routing/domains/librariesPaths";
import { type ObjectState } from "src/utils/formState";
import { type AiFieldProposalFormValue } from "./utils/aiFieldProposalForm";
import { isAiProposedDeletion } from "./utils/bindAiProposal";

type AiProposalValueProps = {
  aiProposal: ObjectState<AiFieldProposalFormValue> | undefined;
  originalValue: ReactNode;
  children: ReactNode;
};

/** Shows the saved value as removed instead of an editable field for entity DELETE proposals. */
export function AiProposalValue(props: AiProposalValueProps) {
  const { aiProposal, originalValue, children } = props;
  const tid = useTestIds(props, "removedValue");
  const isDeletion = useComputed(() => isAiProposedDeletion(aiProposal), [aiProposal]);
  // Beam's public fields overwrite originalValue when proposedValue is absent. Keep the static
  // removal treatment here until Beam supports original-only proposals through those public APIs.
  return isDeletion ? (
    <span {...tid} css={Css.tdlt.gray700.$}>
      {originalValue}
    </span>
  ) : (
    <>{children}</>
  );
}

/** A pending proposal's cited pages, for a field's `helperText` slot. */
export type AiFieldSourcesProps = { afpId: string; pageNumbers: number[] };

/** Once a proposal is accepted or rejected we stop showing its sources, matching the field's AI highlight clearing. */
export function pendingSources(
  afpState: ObjectState<AiFieldProposalFormValue> | undefined,
): AiFieldSourcesProps | undefined {
  if (afpState?.status.value !== AiFieldProposalStatus.Pending) return undefined;
  const { id, pageNumbers } = afpState;
  if (!id.value || !pageNumbers.value?.length) return undefined;
  return { afpId: id.value, pageNumbers: pageNumbers.value };
}

export function AiFieldSources(props: AiFieldSourcesProps) {
  const { afpId, pageNumbers } = props;
  const tid = useTestIds(props, "aiFieldSources");
  // The document is only reachable through `planPackage(id, versionId)`, so link with this page's params.
  // The material catalog shares these fields but has neither params nor a document, so it gets no line.
  const { planPackageId, planPackageVersionId } = useRouteParams<Partial<PlanPackageDetailsParams>>();
  if (!planPackageId || !planPackageVersionId) return null;
  return (
    <span {...tid}>
      Sources:{" "}
      {pageNumbers.map((page, i) => (
        <Fragment key={page}>
          {i > 0 && ", "}
          <a
            href={planPackageAiSourcesRoute.url({ planPackageId, planPackageVersionId }, { afpId, page: String(page) })}
            target="_blank"
            rel="noreferrer"
            css={Css.xsSb.blue600.tdn.$}
            {...tid.page}
          >
            Page {page}
          </a>
        </Fragment>
      ))}
    </span>
  );
}
